// Dedicated content for the Digital & Commerce capability page.
// Rendered by pages/DigitalPage.jsx at /capabilities/digital-commerce

export const DIGITAL_PAGE = {
  slug: "digital-commerce",
  title: "Digital & Commerce",
  metaTitle: "Digital Marketing & E-Commerce Consultants India | BLEZZINGS",
  metaDescription:
    "Digital and e-commerce consulting in India — performance marketing, Amazon and Flipkart, D2C, CRO, CRM, SEO and AI search, built around profitable growth.",
  heroSubhead: "Turn digital investment into customers, revenue and repeat business.",
  heroParas: [
    "Digital growth does not come from running more campaigns.",
    "It comes from connecting paid media, marketplaces, D2C, search, social, conversion and retention into one commercial system — with every investment accountable to customer and business outcomes.",
    "BLEZZINGS helps businesses acquire customers more efficiently, accelerate e-commerce, improve digital conversion and increase customer lifetime value across the entire digital growth engine.",
  ],
  heroTagline: "Performance. Commerce. Conversion. Retention.",
  heroCta: "Discuss Your Digital Growth",

  challengeLead: "Digital activity is everywhere. Profitable digital growth is much harder.",
  challengeIntro:
    "Businesses today operate across an expanding ecosystem:",
  challengeEcosystem: [
    "Google", "Meta", "YouTube", "Marketplaces", "D2C websites", "Search", "Social media",
    "Influencers", "CRM", "WhatsApp", "Email", "Quick commerce", "Retail media",
  ],
  challengeTeamsLead:
    "Yet these channels are often managed by different teams and agencies, each optimising its own metrics.",
  challengeTeams: [
    "Performance teams chase ROAS.",
    "Marketplace teams chase sales.",
    "Social teams chase engagement.",
    "SEO teams chase traffic.",
    "CRM teams chase open rates.",
    "Website teams chase conversion.",
  ],
  challengeNeedLead: "But the business needs something more fundamental:",
  challengeNeed: "Profitable customer and revenue growth.",
  challengeProblemsLead: "When the digital system is fragmented, familiar problems emerge:",
  challengeProblems: [
    "Customer acquisition costs keep rising",
    "Performance campaigns stop scaling",
    "Marketplace sales grow but margins deteriorate",
    "Website traffic does not convert",
    "Brand and performance marketing work separately",
    "Customers are acquired but rarely retained",
    "Marketplace and D2C pricing conflict",
    "Organic traffic lacks commercial intent",
    "Social engagement generates little business impact",
    "Agencies optimise channels rather than the total customer economics",
    "Management struggles to understand what digital investment is really producing",
  ],
  challengeClosing: [
    "The problem is rarely one channel in isolation.",
    "It is usually the system connecting them.",
  ],

  perspectiveLead: "Digital performance is usually constrained by more than media performance.",
  perspectiveIntro:
    "When customer acquisition becomes expensive, the obvious response is to change:",
  perspectiveResponse: ["Targeting", "Bids", "Platforms", "Creative", "Media allocation"],
  perspectiveMid: "Sometimes that is the answer. Often it isn't.",
  perspectiveConstraintLead: "The real constraint may be:",
  perspectiveConstraints: [
    "The wrong customer",
    "A weak proposition",
    "Poor positioning",
    "The wrong price",
    "Undifferentiated creative",
    "Weak ratings and reviews",
    "An unconvincing product page",
    "Website friction",
    "Slow checkout",
    "Poor marketplace availability",
    "Weak repeat purchase",
    "Low customer lifetime value",
    "Or economics that simply do not support the acquisition cost",
  ],
  perspectiveSystemLead:
    "That is why BLEZZINGS approaches Digital & Commerce as a commercial growth system, not a collection of digital services. We connect:",
  perspectiveHighlight:
    "Customer → Proposition → Traffic → Conversion → Transaction → Retention → Lifetime Value",
  perspectiveClosing: [
    "The objective is not simply to improve digital metrics.",
    "It is to improve the economics of digital growth.",
  ],

  offersLead: "Six capabilities. One connected digital growth engine.",
  offers: [
    {
      slug: "performance-marketing-paid-media",
      name: "Performance Marketing & Paid Media",
      lead: "Acquire customers profitably — and scale what works.",
      body: "Paid media can create growth quickly. It can also consume enormous amounts of money quickly. BLEZZINGS helps businesses plan, activate and optimise performance marketing around the metrics that actually matter — customer acquisition, qualified demand, revenue, contribution and return on investment.",
      list: [
        "Performance marketing strategy", "Paid search", "Google Ads", "Meta Ads", "YouTube advertising",
        "LinkedIn advertising", "Shopping campaigns", "Programmatic media", "Lead generation",
        "Customer acquisition", "Remarketing and retargeting", "Audience strategy", "Media planning and buying",
        "Campaign architecture", "Landing-page optimisation", "Creative testing", "Funnel optimisation",
        "CAC optimisation", "ROAS improvement", "Profitability analysis", "Attribution and measurement",
      ],
      closing: "The objective is not simply to lower cost-per-click. It is to build a scalable customer-acquisition engine.",
    },
    {
      slug: "ecommerce-marketplace-growth",
      name: "E-Commerce & Marketplace Growth",
      lead: "Win where customers are already shopping.",
      body: "Marketplaces provide enormous customer access — but success increasingly requires much more than uploading a catalogue. BLEZZINGS helps businesses grow across Amazon, Flipkart, Noon, quick-commerce platforms and other relevant marketplaces by connecting assortment, content, pricing, advertising, reviews, inventory and commercial economics.",
      list: [
        "Marketplace strategy", "Amazon growth", "Flipkart growth", "Noon marketplace growth",
        "Quick-commerce strategy", "Marketplace account management", "Catalogue optimisation",
        "Listing optimisation", "Marketplace SEO", "Product-detail-page optimisation", "Marketplace content",
        "Amazon advertising", "Flipkart advertising", "Noon advertising", "Retail media", "Pricing and promotions",
        "Assortment strategy", "Ratings and reviews", "Inventory planning", "Availability", "Marketplace analytics",
        "Sales optimisation", "Contribution-margin improvement", "Marketplace profitability",
      ],
      closing: "Marketplace growth should not be measured by GMV alone. The objective is to build sustainable, profitable marketplace scale.",
    },
    {
      slug: "d2c-cro-digital-experience",
      name: "D2C, CRO & Digital Experience",
      lead: "Turn your owned digital channels into conversion engines.",
      body: "A D2C website gives businesses something marketplaces cannot fully provide: ownership of the customer relationship. But owning the channel creates value only when visitors become customers — and customers keep coming back. BLEZZINGS helps businesses design and optimise digital experiences that reduce friction and improve conversion.",
      list: [
        "D2C strategy", "E-commerce website strategy", "Website planning", "UX/UI", "Digital customer journeys",
        "Conversion Rate Optimisation", "Landing pages", "Product-detail pages", "Navigation and merchandising",
        "Search and discovery", "Offer architecture", "Bundles and upselling", "Cart optimisation",
        "Checkout optimisation", "Trust and social proof", "A/B testing", "Website analytics",
        "Conversion diagnostics", "Mobile commerce", "Website performance", "Digital experience optimisation",
      ],
      closing: "We look beyond aesthetics. Every page should help the customer move closer to a decision.",
    },
    {
      slug: "crm-retention-loyalty",
      name: "CRM, Retention & Loyalty",
      lead: "Turn first purchases into lasting customer value.",
      body: "Acquisition becomes increasingly expensive when every customer has to be bought again. BLEZZINGS helps businesses build customer lifecycle systems that increase repeat purchase, frequency, retention and lifetime value.",
      list: [
        "CRM strategy", "Customer lifecycle strategy", "Marketing automation", "Email marketing",
        "WhatsApp marketing", "SMS marketing", "Customer segmentation", "Personalisation", "Welcome journeys",
        "Lead-nurture journeys", "Abandoned-cart recovery", "Post-purchase communication", "Cross-sell", "Upsell",
        "Repeat-purchase programmes", "Loyalty programmes", "Win-back campaigns", "Churn reduction",
        "Customer Lifetime Value optimisation", "CRM analytics", "Retention measurement",
      ],
      closing: "A high-performing digital business does not only ask: “How much did it cost to acquire this customer?” It also asks: “How much value can we create from this relationship over time?”",
    },
    {
      slug: "seo-content-ai-search",
      name: "SEO, Content & AI Search",
      lead: "Be discoverable when customers are actively looking for answers, solutions and products.",
      body: "Search behaviour is changing. Customers increasingly discover businesses through traditional search engines, marketplaces and AI-driven answer and discovery experiences. BLEZZINGS helps businesses build organic visibility around commercial intent, customer questions and topics they have the credibility to own.",
      list: [
        "SEO strategy", "Technical SEO", "On-page SEO", "Commercial keyword strategy", "High-intent keyword targeting",
        "Content SEO", "E-commerce SEO", "Product-page SEO", "Local SEO where relevant", "Search-intent mapping",
        "Content strategy", "Topic authority", "Answer-engine optimisation", "AI-search visibility",
        "Structured content", "FAQ architecture", "Thought-leadership content", "Search-led editorial planning",
        "Organic performance measurement",
      ],
      closing: "The objective is not simply more website traffic. It is greater visibility when commercially relevant customers are searching.",
    },
    {
      slug: "social-influencer-creator-marketing",
      name: "Social, Influencer & Creator Marketing",
      lead: "Build relevance and demand where customers spend their attention.",
      body: "Social platforms shape discovery, conversation, credibility and increasingly commerce itself. But content volume and follower counts are poor substitutes for strategy. BLEZZINGS helps businesses connect social and creator activity to clear brand and commercial objectives.",
      list: [
        "Social media strategy", "Platform strategy", "Content strategy", "Social content", "Always-on communication",
        "Community management", "Influencer strategy", "Influencer identification", "Creator partnerships",
        "Influencer campaigns", "User-generated content", "Advocacy", "Social commerce", "Campaign amplification",
        "Creator content for paid media", "Social listening", "Performance measurement",
      ],
      closing: "The objective is not simply engagement. It is to create relevance, trust, demand and advocacy around the brand.",
    },
    {
      slug: "amazon-marketplace-growth",
      name: "Amazon Marketplace Growth & Management",
      lead: "Win the shelf that never closes.",
      body: "Amazon is often a brand's largest digital storefront — and its least understood P&L. BLEZZINGS helps consumer brands, manufacturers and D2C businesses grow on Amazon through connected strategy and execution: listings, advertising, pricing, portfolio, promotions and analytics managed against contribution, not just revenue.",
      list: [
        "Marketplace account management", "Listing optimisation", "Amazon advertising", "Pricing and promotions",
        "Portfolio and assortment strategy", "Content and A+ pages", "Reviews and ratings", "Inventory and availability",
        "Marketplace analytics", "Contribution-margin improvement",
      ],
      closing: "The goal is not simply more marketplace sales. It is profitable, defensible marketplace growth.",
    },
    {
      slug: "d2c-performance-marketing",
      name: "Performance Marketing for Consumer Brands",
      lead: "Acquisition that pays for itself.",
      body: "For a D2C or consumer brand, performance marketing is not a channel — it is the engine room of the P&L. BLEZZINGS connects Meta and Google campaigns, creative testing, conversion and retention to CAC, contribution margin and lifetime value.",
      list: [
        "Meta and Google Ads", "Creative testing", "Conversion rate optimisation", "Landing-page strategy",
        "Retention and repeat purchase", "CAC optimisation", "Contribution-margin management", "Attribution and measurement",
      ],
      closing: "We optimise for customers who pay back, not clicks that look good in a dashboard.",
    },
  ],

  connectLead: "Digital channels should not compete with one another for credit.",
  connectJourney:
    "A customer may discover the brand on Instagram, search for it on Google, read reviews on Amazon, visit the website, watch a YouTube review, return through a Meta retargeting ad, buy on Noon, join a WhatsApp list — and purchase again three months later.",
  connectQuestion: "Which channel created the customer?",
  connectAnswer:
    "The wrong answer is to force every interaction into a single-channel explanation. The better question is: how did the entire digital system help move the customer from discovery to purchase to repeat business?",
  connectStagesLead: "That is why we connect:",
  connectStages: [
    { name: "Discover", channels: "SEO · Content · Social · Influencers · YouTube" },
    { name: "Acquire", channels: "Paid Search · Paid Social · Shopping · Retail Media" },
    { name: "Convert", channels: "D2C · Marketplaces · CRO · Product Pages · Reviews" },
    { name: "Retain", channels: "CRM · Email · WhatsApp · Loyalty · Automation" },
    { name: "Grow", channels: "Repeat Purchase · Cross-Sell · Lifetime Value · Advocacy" },
  ],
  connectClosing: [
    "Different channels perform different jobs.",
    "They should ultimately contribute to the same commercial objective.",
  ],

  approachLead: "From digital diagnosis to profitable growth.",
  approach: [
    {
      title: "Discover",
      body: "We understand the business economics before optimising the digital channels. We assess:",
      list: ["Customers", "Proposition", "Product portfolio", "Pricing", "Competitors", "Acquisition", "Marketplaces", "Website", "Conversion", "CRM", "Retention", "Channel economics", "Existing performance"],
    },
    {
      title: "Strategize",
      body: "We determine where the biggest growth constraints and opportunities exist. That may involve choices around:",
      list: ["Customer segments", "Channel roles", "Media investment", "Marketplace strategy", "D2C", "Conversion", "Search", "CRM", "Retention"],
    },
    {
      title: "Design",
      body: "We build the required digital growth system:",
      list: ["Campaign architecture", "Funnel", "Marketplace model", "Digital journey", "CRM journeys", "Search strategy", "Content system", "Measurement framework"],
    },
    {
      title: "Activate",
      body: "We execute across the channels and capabilities required to turn the strategy into market performance.",
    },
    {
      title: "Optimize",
      body: "We continuously measure:",
      list: ["Customer acquisition", "Conversion", "Revenue", "Repeat purchase", "Lifetime value", "Contribution", "Profitability"],
      after: "Investment is progressively shifted toward what creates the greatest commercial value.",
    },
  ],
  approachClosing: "Discover → Strategize → Design → Activate → Optimize",

  deliverLead: "Strategy, execution and measurement — connected.",
  deliverIntro: "Depending on the engagement, deliverables may include:",
  deliverGroups: [
    {
      label: "Performance Marketing",
      items: ["Media strategy", "Campaign architecture", "Audience strategy", "Search campaigns", "Paid social campaigns", "Shopping campaigns", "Retargeting", "Landing pages", "Creative testing frameworks", "CAC and ROAS dashboards"],
    },
    {
      label: "Marketplaces",
      items: ["Marketplace strategy", "Amazon / Flipkart / Noon growth plans", "Catalogue optimisation", "Listing optimisation", "Marketplace SEO", "Marketplace advertising", "Pricing and promotions", "Ratings and reviews strategy", "Inventory recommendations", "Marketplace profitability dashboard"],
    },
    {
      label: "D2C & CRO",
      items: ["D2C growth strategy", "Website diagnostic", "UX recommendations", "Customer-journey maps", "Landing-page architecture", "PDP optimisation", "Conversion diagnostic", "CRO roadmap", "Testing plan", "Conversion dashboard"],
    },
    {
      label: "CRM & Retention",
      items: ["CRM strategy", "Customer lifecycle framework", "Segmentation", "Automated journeys", "Email / WhatsApp programmes", "Loyalty strategy", "Cross-sell and upsell frameworks", "Retention dashboard", "CLV framework"],
    },
    {
      label: "SEO & Content",
      items: ["SEO audit", "Keyword architecture", "Commercial-intent strategy", "Technical SEO roadmap", "Content strategy", "Topic clusters", "AI-search / answer-engine recommendations", "Organic measurement dashboard"],
    },
    {
      label: "Social & Influence",
      items: ["Social strategy", "Content pillars", "Platform plan", "Influencer strategy", "Creator shortlists", "Campaign concepts", "Social-commerce recommendations", "Measurement framework"],
    },
  ],
  deliverClosing: "The precise scope depends on where the growth constraint sits.",

  outcomesLead: "Digital should move business metrics — not just platform metrics.",
  outcomesIntro: "Depending on the engagement, our work is designed to help create:",
  outcomes: [
    "Lower customer acquisition cost",
    "Higher ROAS",
    "Greater qualified demand",
    "Higher digital conversion",
    "Greater marketplace sales",
    "Improved marketplace profitability",
    "Higher D2C revenue",
    "Improved contribution margin",
    "Stronger organic discoverability",
    "Greater repeat purchase",
    "Higher retention",
    "Increased customer lifetime value",
    "Stronger brand-to-performance integration",
    "Better digital measurement",
    "More efficient allocation of marketing investment",
  ],
  outcomesClosing: [
    "We care about clicks, impressions and engagement only to the extent that they help explain or improve a meaningful commercial outcome.",
  ],

  whenLead: "Bring us in when digital growth is becoming harder, more expensive or more fragmented.",
  whenIntro: "Digital & Commerce engagements are particularly relevant when:",
  whenBullets: [
    "Customer acquisition costs are rising",
    "Performance campaigns have stopped scaling",
    "ROAS is declining",
    "Marketplace sales are growing but profits are not",
    "You want to build or accelerate Amazon, Flipkart or Noon",
    "You are entering GCC e-commerce markets",
    "Your D2C website receives traffic but converts poorly",
    "You are overdependent on marketplaces",
    "Your brand is overdependent on paid acquisition",
    "SEO generates traffic but little business",
    "Repeat purchase is weak",
    "CRM exists but is underutilised",
    "Social activity lacks commercial direction",
    "Influencer campaigns produce uncertain ROI",
    "Brand and performance marketing operate separately",
    "Multiple digital agencies are working in silos",
    "You need to build a digital growth engine from the ground up",
  ],
  whenAudience:
    "We typically work with CEOs, founders, CMOs, business heads, e-commerce leaders, digital leaders, growth teams, category teams and P&L owners.",

  whyLead: "We optimise the growth system — not just the campaign.",
  whyIntro: [
    "Different specialists naturally see the problem through their own lens.",
  ],
  whyLenses: [
    "A media agency sees media.",
    "An e-commerce agency sees marketplaces.",
    "A web agency sees the website.",
    "An SEO agency sees search.",
    "A CRM agency sees retention.",
  ],
  whyConnect:
    "But customers move across all of them. BLEZZINGS connects the entire commercial journey.",
  whyBlocks: [
    { name: "Business Economics Before Channel Metrics", paras: ["We start with the economics of the business and customer rather than optimising platform metrics in isolation."] },
    { name: "Customer Strategy Before Targeting", paras: ["Our Market & Customer Intelligence capability can identify who the business should pursue and what actually drives their decisions."] },
    { name: "Brand + Performance", paras: ["We connect proposition, positioning and creative with performance acquisition rather than forcing brand and performance teams into separate worlds."] },
    { name: "Commerce + Conversion", paras: ["Marketplace, D2C and CRO decisions are treated as interconnected parts of the transaction system."] },
    { name: "Acquisition + Retention", paras: ["We evaluate customer acquisition against repeat purchase and lifetime value — because sustainable growth cannot depend on continuously reacquiring customers."] },
    { name: "Strategy + Execution", paras: ["We don't have to stop after recommending what should change. BLEZZINGS can help activate, operate and optimise the digital growth system."] },
  ],
  whyClosing: "One partner. From click to customer to lifetime value.",

  relatedLead: "Digital performs better when the commercial fundamentals are right.",
  related: [
    { title: "Strategy & Commercial Transformation", body: "Connect digital growth with portfolio, pricing, channels, go-to-market and sales strategy.", to: "/capabilities/strategy-commercial-transformation" },
    { title: "Market & Customer Intelligence", body: "Understand customer segments, purchase drivers, decision journeys, competitors and market opportunities before investing in acquisition.", to: "/capabilities/market-customer-intelligence" },
    { title: "Brand, Marketing & Communications", body: "Strengthen positioning, propositions, advertising, content and creative before scaling digital investment.", to: "/capabilities/brand-marketing-communications" },
  ],

  faqs: [
    { q: "What does Digital & Commerce consulting include?", a: "Digital & Commerce consulting can include performance marketing, marketplaces, D2C, conversion optimisation, CRM, retention, SEO, content, social media and influencer marketing. BLEZZINGS connects these capabilities around one commercial growth strategy." },
    { q: "Does BLEZZINGS manage performance marketing campaigns?", a: "Yes. Depending on the engagement, we can support strategy, campaign architecture, media planning, targeting, paid search, paid social, remarketing, landing-page optimisation, creative testing and performance optimisation." },
    { q: "Which advertising platforms do you work with?", a: "Depending on the customer and market, campaigns may include platforms such as Google, Meta, YouTube, LinkedIn and relevant marketplace or retail-media platforms. The platform should follow the customer strategy — not the other way around." },
    { q: "Can BLEZZINGS manage Amazon, Flipkart and Noon marketplaces?", a: "Yes. Marketplace engagements can cover strategy, listings, catalogue optimisation, marketplace SEO, advertising, pricing, promotions, ratings and reviews, inventory considerations, analytics and profitability. We include Amazon, Flipkart and Noon alongside other relevant e-commerce and quick-commerce platforms depending on geography and category." },
    { q: "Can you help businesses sell on Noon in the GCC?", a: "Yes. Noon can be incorporated into broader GCC marketplace and e-commerce strategy, including marketplace setup, catalogue, product-page optimisation, advertising, pricing, promotions and growth planning." },
    { q: "What is the difference between D2C and marketplace strategy?", a: "With marketplaces, the business operates largely within another company's customer and commerce ecosystem. With D2C, the business owns more of the customer experience, first-party relationship and transaction environment. The economics, acquisition strategy, customer data and operating models therefore differ significantly." },
    { q: "Does BLEZZINGS build e-commerce websites?", a: "Depending on scope, we can support D2C website strategy, customer journeys, UX/UI, e-commerce architecture, conversion optimisation and implementation. Our focus is not simply website development. It is building a digital experience that converts." },
    { q: "What is Conversion Rate Optimisation?", a: "CRO is the systematic improvement of digital experiences to increase the proportion of visitors who complete desired actions such as enquiries, registrations or purchases. It can involve customer research, page design, messaging, offers, product pages, checkout, trust signals and testing." },
    { q: "Can BLEZZINGS help reduce customer acquisition cost?", a: "Yes. But CAC should not always be treated purely as a media problem. We examine targeting, proposition, creative, landing pages, website conversion, pricing, marketplace performance and retention because all of these can affect acquisition economics." },
    { q: "Do you provide CRM and marketing automation?", a: "Yes. CRM and retention work can include lifecycle strategy, segmentation, email, WhatsApp, automation, abandoned-cart journeys, cross-sell, repeat purchase, loyalty and win-back programmes." },
    { q: "Do you provide SEO?", a: "Yes. SEO engagements can include technical SEO, commercial keyword strategy, on-page optimisation, e-commerce SEO, content strategy, topic authority and organic performance measurement." },
    { q: "What is AI Search optimisation?", a: "Customers increasingly discover information through AI-enabled search and answer experiences in addition to conventional search engines. We help structure authoritative, useful and clearly organised content around customer questions and commercially relevant topics so the business is better positioned to be discovered across evolving search environments." },
    { q: "How does BLEZZINGS measure digital success?", a: "The measurement framework depends on the business model. Metrics may include CAC, ROAS, revenue, conversion, contribution margin, marketplace profitability, qualified leads, repeat purchase, retention and Customer Lifetime Value. Channel metrics are useful, but the ultimate objective is measurable business performance." },
  ],

  ctaHeading: "Build a digital growth engine that does more than generate clicks.",
  ctaBody:
    "Whether you need to improve performance marketing, scale marketplaces, accelerate D2C, increase conversion or build stronger retention, BLEZZINGS connects the entire digital customer journey around one commercial objective: profitable growth.",
  ctaLabel: "Discuss Your Digital Growth",
};
